/**
 * React Query hooks for Trade operations.
 *
 * T061 [US3]: Create useTrades React Query hook for manual trade CRUD.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTrade,
  createTrade,
  updateTrade,
  deleteTrade,
  type Trade,
  type CreateTradeRequest,
  type UpdateTradeRequest,
} from '../../../services/tradesApi';
import { getTrades } from '../../../services/portfolioApi';

/**
 * Hook to fetch a single trade by ID.
 *
 * @param tradeId - Trade ID to fetch
 * @param enabled - Whether to enable query (default: true)
 */
export function useTrade(tradeId: number, enabled: boolean = true) {
  return useQuery<Trade>({
    queryKey: ['trade', tradeId],
    queryFn: () => getTrade(tradeId),
    enabled: enabled && !!tradeId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Hook to create a new trade.
 * T061 [US3]: Create useCreateTrade mutation hook.
 * Automatically refetches the trades cache on success.
 */
export function useCreateTrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tradeData: CreateTradeRequest) => {
      return await createTrade(tradeData);
    },
    onSuccess: async (data) => {
      // Refetch only existing query keys in parallel
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['positionTrades'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['positions'], type: 'active' }),
        // Allocation % and base-currency totals derive from the portfolio total;
        // PositionTable derives per-currency FX from its currency_breakdown. Use
        // invalidate (not refetch type:'active') so it also refreshes when
        // PositionTable is unmounted (e.g. on the Trades tab).
        queryClient.invalidateQueries({ queryKey: ['portfolioTotal'] }),
        // A trade may migrate a watchlist thesis onto the new position and
        // delete its watchlist row (see migrate_watchlist_thesis).
        queryClient.invalidateQueries({ queryKey: ['watchlist'] }),
        queryClient.invalidateQueries({ queryKey: ['watchlistThesis'] }),
        queryClient.invalidateQueries({ queryKey: ['positionThesis'] }),
      ]);
    },
    onError: (error: Error) => {
      console.error('Failed to create trade:', error);
      throw error; // Re-throw to let component handle UI feedback
    },
  });
}

/**
 * Hook to update an existing trade.
 * T061 [US3]: Create useUpdateTrade mutation hook.
 * Automatically refetches cache and recalculates P&L.
 */
export function useUpdateTrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tradeId, params }: { tradeId: number; params: UpdateTradeRequest }) => {
      return await updateTrade(tradeId, params);
    },
    onSuccess: async (_, variables) => {
      // Refetch only existing query keys in parallel
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['positionTrades'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['trade', variables.tradeId], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['positions'], type: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['portfolioTotal'] }),
      ]);
    },
    onError: (error: Error) => {
      console.error('Failed to update trade:', error);
      throw error;
    },
  });
}

/**
 * Hook to delete a trade.
 * T061 [US3]: Create useDeleteTrade mutation hook.
 * Automatically refetches the trades cache on success.
 */
export function useDeleteTrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tradeId: number) => {
      await deleteTrade(tradeId);
    },
    onSuccess: async () => {
      // Refetch only existing query keys in parallel
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['positionTrades'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['positions'], type: 'active' }),
        // Allocation % and base-currency totals derive from the portfolio total;
        // PositionTable derives per-currency FX from its currency_breakdown. Use
        // invalidate (not refetch type:'active') so it also refreshes when
        // PositionTable is unmounted (e.g. on the Trades tab).
        queryClient.invalidateQueries({ queryKey: ['portfolioTotal'] }),
      ]);
    },
    onError: (error: Error) => {
      console.error('Failed to delete trade:', error);
      throw error;
    },
  });
}

/**
 * Hook to fetch trades for a specific position (by symbol).
 *
 * @param symbol - Stock symbol to fetch trades for
 * @param enabled - Whether to enable query (default: true)
 */
export function usePositionTrades(symbol: string | null, enabled: boolean = true) {
  return useQuery<Trade[]>({
    queryKey: ['positionTrades', symbol],
    queryFn: () => getTrades(symbol || ''),
    enabled: enabled && !!symbol,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds (reduced from 2 minutes)
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnReconnect: true, // Refetch on network reconnect
  });
}

/**
 * Hook to create a corporate action.
 * Automatically refetches the trades cache on success.
 */
export function useCreateCorporateAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (caData: import('../../../services/tradesApi').CorporateActionRequest) => {
      const { createCorporateAction } = await import('../../../services/tradesApi');
      return await createCorporateAction(caData);
    },
    onSuccess: async () => {
      // Refetch only existing query keys in parallel
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['positionTrades'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['positions'], type: 'active' }),
        // Allocation % and base-currency totals derive from the portfolio total;
        // PositionTable derives per-currency FX from its currency_breakdown. Use
        // invalidate (not refetch type:'active') so it also refreshes when
        // PositionTable is unmounted (e.g. on the Trades tab).
        queryClient.invalidateQueries({ queryKey: ['portfolioTotal'] }),
      ]);
    },
    onError: (error: Error) => {
      console.error('Failed to create corporate action:', error);
      throw error;
    },
  });
}
